import { useCallback, useEffect, useRef, useState } from 'react';
import { getSupabaseClient } from '../supabase-client';
import { reportError } from '../../lib/monitoring';
import type { SolMemoryRow } from '../database.types';

/**
 * WHAT SOL REMEMBERS — the read. M11 / D-040.
 *
 * The caller's ACTIVE memories, newest first, under owner-only forced RLS.
 * Superseded and deleted rows are filtered by the query and never shown; a
 * memory he forgot is gone from this screen the moment the writer returns.
 *
 * **A LIST FOR A PERSON, NOT A PROMPT FOR A MODEL.** What Sol is actually shown
 * on a turn is decided on the server — bounded and relevance-gated — from the
 * same rows. Nothing read here is sent anywhere.
 */

/** The exact columns the screen needs. Never a star. */
export const SOL_MEMORY_LIST_COLUMNS =
  'id, owner_user_id, kind, origin, content, state, source_conversation_id, project_id, created_at, updated_at, deleted_at' as const;

export const SOL_MEMORY_LIST_LIMIT = 200;

export type SolMemoriesStatus = 'loading' | 'ready' | 'failed';

export interface SolMemoriesState {
  readonly status: SolMemoriesStatus;
  readonly memories: readonly SolMemoryRow[];
  readonly error: string | null;
  readonly reload: () => Promise<void>;
}

type MemoriesFetch = { kind: 'ready'; rows: SolMemoryRow[] } | { kind: 'failed' };

export function useSolMemories(): SolMemoriesState {
  const client = getSupabaseClient();
  const [status, setStatus] = useState<SolMemoriesStatus>(client ? 'loading' : 'failed');
  const [memories, setMemories] = useState<readonly SolMemoryRow[]>([]);
  const [error, setError] = useState<string | null>(
    client ? null : 'Crewlink is not connected to a server yet.',
  );
  /** Pure fetch. Sets no state — the house pattern. */
  const fetchMemories = useCallback(async (): Promise<MemoriesFetch> => {
    if (!client) return { kind: 'failed' };
    const { data, error: queryError } = await client
      .from('sol_memory')
      .select(SOL_MEMORY_LIST_COLUMNS)
      .eq('state', 'active')
      .order('updated_at', { ascending: false })
      .limit(SOL_MEMORY_LIST_LIMIT);
    if (queryError) {
      reportError(queryError, 'sol-memories.load');
      return { kind: 'failed' };
    }
    return { kind: 'ready', rows: data ?? [] };
  }, [client]);

  const apply = useCallback((result: MemoriesFetch) => {
    if (result.kind === 'failed') {
      setMemories([]);
      setError('Could not load what Sol remembers.');
      setStatus('failed');
      return;
    }
    setMemories(result.rows);
    setError(null);
    setStatus('ready');
  }, []);

  const generation = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    generation.current += 1;
    const ticket = generation.current;
    const result = await fetchMemories();
    if (ticket !== generation.current) return;
    apply(result);
  }, [fetchMemories, apply]);

  useEffect(() => {
    void load();
  }, [load]);

  return { status, memories, error, reload: load };
}
